// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { useChainDetailQuery } from "@/api/hooks/use-chains";
import { ChainLogo, chainHasLogo } from "@/components/shared/chain-link";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { KeyChip } from "@/components/shared/key-chip";
import { TimezoneCell } from "@/components/shared/timezone-cell";
import { Separator } from "@/components/ui/separator";

interface ChainInfoTabProps {
  chainName: string;
}

export function ChainInfoTab({ chainName }: ChainInfoTabProps) {
  const { t } = useTranslation("back-office");
  const { data: chain } = useChainDetailQuery(chainName);

  if (!chain) return null;

  const showLogo = chainHasLogo(chain.chainName);

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
        <span className="text-muted-foreground">{t("chains.info.fields.displayName")}</span>
        <span>{chain.legibleName}</span>

        <span className="text-muted-foreground">{t("chains.info.fields.chainName")}</span>
        <span>
          <KeyChip label={chain.chainName} variant="chain" />
        </span>

        <span className="text-muted-foreground">{t("chains.info.fields.defaultTimeZone")}</span>
        <span>
          {chain.defaultTimeZone ? (
            <TimezoneCell timeZone={chain.defaultTimeZone} />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>

        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("common.createdAt")}</span>
        <DateTimeValue value={chain.createdAt} />

        <span className="text-muted-foreground">{t("common.updatedAt")}</span>
        <DateTimeValue value={chain.updatedAt} />

        {showLogo && (
          <>
            <Separator className="col-span-2" />
            <div className="col-span-2 flex items-center pt-3">
              <ChainLogo chainName={chain.chainName} size={50} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
